import React, { useState, useEffect } from 'react';
import _ from 'lodash';

const JSONCompare = () => {
  const [leftJSON, setLeftJSON] = useState('');
  const [rightJSON, setRightJSON] = useState('');
  const [leftParsed, setLeftParsed] = useState(null);
  const [rightParsed, setRightParsed] = useState(null);
  const [diffResult, setDiffResult] = useState(null);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);

  // Function to sort object keys for consistent comparison
  const sortObjectKeys = (obj) => {
    if (_.isArray(obj)) {
      return obj.map(item => sortObjectKeys(item));
    } else if (_.isObject(obj)) {
      const sortedObj = {};
      Object.keys(obj).sort().forEach(key => {
        sortedObj[key] = sortObjectKeys(obj[key]);
      });
      return sortedObj;
    }
    return obj;
  };

  // Parse JSON safely
  const parseJSON = (jsonStr) => {
    try {
      return JSON.parse(jsonStr);
    } catch (e) {
      return null;
    }
  };

  // Compare two JSON objects and generate diff report
  const compareJSON = (left, right) => {
    setProcessing(true);
    
    // Use setTimeout to allow React to render "Processing..." message
    setTimeout(() => {
      try {
        // Sort keys in both objects for order-insensitive comparison
        const sortedLeft = sortObjectKeys(left);
        const sortedRight = sortObjectKeys(right);
        
        // Generate diff report
        const diff = generateDiff(sortedLeft, sortedRight);
        setDiffResult(diff);
        setError('');
      } catch (e) {
        setError(`Error during comparison: ${e.message}`);
        setDiffResult(null);
      } finally {
        setProcessing(false);
      }
    }, 50);
  };

  // Path tracking for nested objects
  const generateDiff = (left, right, path = '') => {
    // For primitive values or if one side is null/undefined
    if (!_.isObject(left) || !_.isObject(right) || left === null || right === null) {
      if (_.isEqual(left, right)) {
        return { type: 'equal', value: left, path };
      } else {
        return { 
          type: 'changed', 
          leftValue: left, 
          rightValue: right, 
          path 
        };
      }
    }
    
    // For arrays (order matters)
    if (_.isArray(left) && _.isArray(right)) {
      if (left.length === right.length && left.every((val, idx) => _.isEqual(val, right[idx]))) {
        return { type: 'equal', value: left, path };
      }
      
      // Find array differences
      const maxLength = Math.max(left.length, right.length);
      const arrayDiff = [];
      
      for (let i = 0; i < maxLength; i++) {
        const itemPath = `${path}[${i}]`;
        
        if (i >= left.length) {
          // Item exists in right but not in left
          arrayDiff.push({ 
            type: 'added', 
            value: right[i], 
            path: itemPath 
          });
        } else if (i >= right.length) {
          // Item exists in left but not in right
          arrayDiff.push({ 
            type: 'removed', 
            value: left[i], 
            path: itemPath 
          });
        } else {
          // Both have the item, compare them
          arrayDiff.push(generateDiff(left[i], right[i], itemPath));
        }
      }
      
      return { type: 'array', children: arrayDiff, path };
    }
    
    // For objects (key order doesn't matter)
    const allKeys = _.union(Object.keys(left), Object.keys(right)).sort();
    
    if (allKeys.length === 0) {
      return { type: 'equal', value: {}, path };
    }
    
    const objectDiff = {};
    let hasChanges = false;
    
    allKeys.forEach(key => {
      const keyPath = path ? `${path}.${key}` : key;
      
      if (!(key in left)) {
        objectDiff[key] = { type: 'added', value: right[key], path: keyPath };
        hasChanges = true;
      } else if (!(key in right)) {
        objectDiff[key] = { type: 'removed', value: left[key], path: keyPath };
        hasChanges = true;
      } else {
        const valueDiff = generateDiff(left[key], right[key], keyPath);
        if (valueDiff.type !== 'equal') {
          objectDiff[key] = valueDiff;
          hasChanges = true;
        } else {
          objectDiff[key] = { type: 'equal', value: left[key], path: keyPath };
        }
      }
    });
    
    return { 
      type: hasChanges ? 'object' : 'equal', 
      children: objectDiff, 
      path 
    };
  };
  
  // Parse input JSON when content changes
  useEffect(() => {
    const leftObj = parseJSON(leftJSON);
    const rightObj = parseJSON(rightJSON);
    
    setLeftParsed(leftObj);
    setRightParsed(rightObj);
    
    if (leftObj && rightObj) {
      setError('');
    } else {
      if (leftJSON && !leftObj) {
        setError('Invalid JSON in left panel');
      } else if (rightJSON && !rightObj) {
        setError('Invalid JSON in right panel');
      }
    }
  }, [leftJSON, rightJSON]);

  // Render diff with syntax highlighting
  const renderDiff = (diff) => {
    if (!diff) return null;
    
    if (diff.type === 'equal') {
      if (_.isObject(diff.value)) {
        return <span className="text-gray-700">{JSON.stringify(diff.value, null, 2)}</span>;
      }
      return <span className="text-gray-700">{JSON.stringify(diff.value)}</span>;
    }
    
    if (diff.type === 'changed') {
      return (
        <div className="space-y-1">
          <div>
            <span className="bg-red-200 px-1 rounded">- </span>
            <span className="bg-red-200 px-1 rounded">{JSON.stringify(diff.leftValue)}</span>
          </div>
          <div>
            <span className="bg-green-200 px-1 rounded">+ </span>
            <span className="bg-green-200 px-1 rounded">{JSON.stringify(diff.rightValue)}</span>
          </div>
        </div>
      );
    }
    
    if (diff.type === 'added') {
      return (
        <span className="bg-green-200 px-1 rounded">
          {JSON.stringify(diff.value)}
        </span>
      );
    }
    
    if (diff.type === 'removed') {
      return (
        <span className="bg-red-200 px-1 rounded">
          {JSON.stringify(diff.value)}
        </span>
      );
    }
    
    if (diff.type === 'array') {
      return (
        <div>
          [
          <div className="ml-4">
            {diff.children.map((child, idx) => (
              <div key={idx}>
                {renderDiff(child)}{idx < diff.children.length - 1 ? ',' : ''}
              </div>
            ))}
          </div>
          ]
        </div>
      );
    }
    
    if (diff.type === 'object') {
      const keys = Object.keys(diff.children);
      return (
        <div>
          {'{'} 
          <div className="ml-4">
            {keys.map((key, idx) => {
              const child = diff.children[key];
              // Skip equal values to reduce clutter
              if (child.type === 'equal' && keys.length > 10) {
                return idx === keys.length - 1 ? null : null;
              }
              
              return (
                <div key={key}>
                  <span className="text-gray-700">"{key}"</span>: {renderDiff(child)}
                  {idx < keys.length - 1 ? ',' : ''}
                </div>
              );
            })}
          </div>
          {'}'}
        </div>
      );
    }
    
    return null;
  };

  const handleCompare = () => {
    if (leftParsed && rightParsed) {
      compareJSON(leftParsed, rightParsed);
    } else {
      setError('Please provide valid JSON in both panels');
    }
  };

  const handleSampleData = () => {
    const sampleLeft = {
      name: "Product A",
      price: 100,
      features: ["fast", "reliable", "compact"],
      details: {
        manufacturer: "Company X",
        year: 2023,
        colors: ["red", "blue"]
      }
    };
    
    const sampleRight = {
      name: "Product A",
      price: 120,
      features: ["fast", "efficient", "compact"],
      details: {
        year: 2024,
        manufacturer: "Company X",
        colors: ["red", "blue", "green"]
      }
    };
    
    setLeftJSON(JSON.stringify(sampleLeft, null, 2));
    setRightJSON(JSON.stringify(sampleRight, null, 2));
  };

  return (
    <div className="w-full max-w-6xl mx-auto space-y-4">
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <div className="font-medium mb-1">Left JSON</div>
          <textarea
            className="w-full h-64 p-2 border rounded font-mono text-sm"
            value={leftJSON}
            onChange={(e) => setLeftJSON(e.target.value)}
            placeholder="Paste JSON here..."
          />
        </div>
        <div className="flex-1">
          <div className="font-medium mb-1">Right JSON</div>
          <textarea
            className="w-full h-64 p-2 border rounded font-mono text-sm"
            value={rightJSON}
            onChange={(e) => setRightJSON(e.target.value)}
            placeholder="Paste JSON here..."
          />
        </div>
      </div>
      
      <div className="flex justify-between items-center">
        <div className="space-x-2">
          <button
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            onClick={handleCompare}
            disabled={!leftParsed || !rightParsed || processing}
          >
            Compare
          </button>
          <button
            className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
            onClick={handleSampleData}
          >
            Load Sample
          </button>
        </div>
        
        {error && <div className="text-red-500">{error}</div>}
        {processing && <div className="text-blue-500">Processing large JSON, please wait...</div>}
      </div>
      
      {diffResult && !processing && (
        <div className="mt-4">
          <div className="font-medium mb-2">Diff Result (showing only differences):</div>
          <div className="border rounded p-4 bg-gray-50 overflow-auto max-h-96 font-mono text-sm whitespace-pre">
            {renderDiff(diffResult)}
          </div>
          <div className="mt-2 text-sm text-gray-500 flex space-x-4">
            <div className="flex items-center">
              <span className="bg-red-200 w-3 h-3 inline-block mr-1 rounded"></span>
              <span>Removed</span>
            </div>
            <div className="flex items-center">
              <span className="bg-green-200 w-3 h-3 inline-block mr-1 rounded"></span>
              <span>Added</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JSONCompare;